"use client";

import { useState, useMemo } from "react";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type UseMultiSelectFilteringPropsT = {
  options: MultiSelectOptionT[];
};

/**
 * Custom hook for multi-select search and filtering
 *
 * Manages search input state and provides filtered options
 * based on case-insensitive label matching. Memoized for performance.
 *
 * @param props - Hook configuration
 * @returns Search state, handlers, and filtered options
 *
 * @example
 * ```typescript
 * const {
 *   searchValue,
 *   setSearchValue,
 *   filteredOptions
 * } = useMultiSelectFiltering({
 *   options
 * });
 *
 * <CommandInput
 *   value={searchValue}
 *   onValueChange={setSearchValue}
 * />
 *
 * {filteredOptions.map(option => ...)}
 * ```
 */
export function useMultiSelectFiltering({
  options,
}: UseMultiSelectFilteringPropsT) {
  const [searchValue, setSearchValue] = useState("");

  // Filter options based on search value - memoized for performance
  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }

    const normalizedSearch = searchValue.toLowerCase();

    return options.filter((option) => {
      const labelMatches = option.label
        .toLowerCase()
        .includes(normalizedSearch);
      const metaMatches = option.meta
        ?.toLowerCase()
        .includes(normalizedSearch);

      return labelMatches || Boolean(metaMatches);
    });
  }, [options, searchValue]);

  return {
    searchValue,
    setSearchValue,
    filteredOptions,
  };
}
